import React, { useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import moment from "moment";

import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";

import "./TableEmployee.scss";
import { WorkShiftHistoryType } from "./sequence/SetupWorkSchedule";

// Mock formatDate function
const formatDate = (date: moment.Moment) => date.format("DD/MM/YY");

const formatTime = (time: string) =>
  time ? moment(time, ["HH:mm", "HH:mm:ss"]).format("HH:mm") : "";

type ModTimeShiftHistoryProps = {
  items?: WorkShiftHistoryType[] | null;
  open: boolean;
  onClose: () => void;
};

type GroupHistoryType = WorkShiftHistoryType & {
  shift: any;
  shiftIndex: number;
  // For grouping logic
  action_id: string;
  group_id: string;
  shift_id: string;
};

const ModTimeShiftHistory = (props: ModTimeShiftHistoryProps) => {
  const flattenedData = useMemo(() => {
    const result: GroupHistoryType[] = [];

    for (const item of props.items ?? []) {
      // เรียงลำดับ shifts ตามวันที่สร้าง (เก่าไปใหม่)
      const sorted = [...item.shifts].sort((a, b) => a.created.localeCompare(b.created));

      let actionCounter = 0;
      let currentActionId = "";

      for (const [shiftIndex, shift] of sorted.entries()) {
        const actionId = `${item.id}-${shift.action}-${actionCounter}`;

        // ถ้า action เปลี่ยน ให้เพิ่ม counter และสร้าง action_id ใหม่
        if (currentActionId !== actionId) {
          actionCounter++;
          currentActionId = `${item.id}-${shift.action}-${actionCounter}`;
        }

        result.push({
          ...item,
          created: shift.created,
          shift,
          shiftIndex,
          // For grouping logic
          action_id: currentActionId,
          group_id: item.id,
          shift_id: `${actionId}-${shiftIndex}`,
        });
      }
    }

    return result;
  }, [props.items]);

  return (
    <ModConfirmCNMI
      backgroundColor="rgba(93, 188, 210, 1)"
      customGridGroup={{ display: "none" }}
      modalStyle={{ minWidth: "55rem" }}
      openModal={props.open}
      titleName="Shift time change history"
      onCloseWithDimmerClick={props.onClose}
      content={
        <div className="table-st-history" style={{ marginBottom: "-2rem" }}>
          <Table celled compact structured>
            <TableHeader>
              <TableRow>
                <TableHeaderCell textAlign="center" width={5}>
                  Created date/time
                </TableHeaderCell>
                <TableHeaderCell textAlign="center" width={5}>
                  Shift time
                </TableHeaderCell>
                <TableHeaderCell textAlign="center" width={3}>
                  Total work hour/day
                </TableHeaderCell>
                <TableHeaderCell textAlign="center" width={3}>
                  Action
                </TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {flattenedData.length === 0 ? (
                <TableCell style={{ color: "grey" }} colSpan="4" textAlign="center">
                  No rows found
                </TableCell>
              ) : null}

              {flattenedData.map((row, index) => {
                // Group rows by employee
                const isFirstRowForEmployee =
                  index === 0 || flattenedData[index - 1].group_id !== row.group_id;
                const rowsForThisEmployee = flattenedData.filter(
                  (d) => d.group_id === row.group_id
                ).length;

                // Group rows by action
                const isFirstRowForAction =
                  index === 0 || flattenedData[index - 1].action_id !== row.action_id;
                const rowsForThisAction = flattenedData.filter(
                  (d) => d.action_id === row.action_id
                ).length;

                // Group rows by action index
                const isFirstRowForActionIndex =
                  index === 0 || flattenedData[index - 1].shift_id !== row.shift_id;
                const shiftsForThisActionIndex = flattenedData.filter(
                  (d) => d.shift_id === row.shift_id
                );

                const totalHours = shiftsForThisActionIndex.reduce(
                  (result, item) => result + Number(item.shift.total_hours),
                  0
                );

                return (
                  <TableRow key={row.action_id}>
                    {/* Created date/time - merge for all shifts of this employee */}
                    {isFirstRowForEmployee && (
                      <TableCell rowSpan={rowsForThisEmployee} textAlign="left" verticalAlign="top">
                        <div>
                          <div>{row.edit_user_full_name}</div>
                          <div>
                            {formatDate(moment(row.created))} ,{" "}
                            {moment(row.created).format("HH:mm")} น.
                          </div>
                        </div>
                      </TableCell>
                    )}

                    {/* Shift time - individual row for each shift */}
                    <TableCell textAlign="left" verticalAlign="top">
                      <div>
                        <div>
                          {row.shift.name} {formatTime(row.shift.start_time)} -{" "}
                          {formatTime(row.shift.end_time)}
                        </div>
                        <div>
                          Break : {formatTime(row.shift.start_break_time)} -{" "}
                          {formatTime(row.shift.end_break_time)}
                        </div>
                      </div>
                    </TableCell>

                    {/* Total work hour/day - merge for all shifts of this employee */}
                    {isFirstRowForActionIndex && (
                      <TableCell rowSpan={shiftsForThisActionIndex.length} textAlign="center">
                        <div>
                          <div>{totalHours} hr</div>
                        </div>
                      </TableCell>
                    )}

                    {/* Action - merge for all rows of this action */}
                    {isFirstRowForAction && (
                      <TableCell
                        rowSpan={rowsForThisAction}
                        textAlign="center"
                        verticalAlign="middle"
                      >
                        {row.shift.action}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      }
    />
  );
};

ModTimeShiftHistory.displayName = "ModTimeShiftHistory";

export default React.memo(ModTimeShiftHistory);
